import { AnalyticAppName, Platforms } from '@core/constants';
import { isHeapActive } from '@core/app-settings';

import { AnalyticCheckinEventTypes } from '@core/model/analytic/AnalyticCheckinEventTypes';
import { ApplicationStateService } from '@core/services/application-state.service';
import { DeviceDetectorService } from 'ngx-device-detector';
import { Guid } from 'guid-typescript';
import { HeapService } from './heap.service';
import { Injectable } from '@angular/core';
import { VisitStateService } from '@core/state/visit-state.service';
import { combineLatest } from 'rxjs';
import { environment } from '@env';
import { isHostedInWin } from '@core/win-bridge/win.utils';
import { AnalyticBaseProperties } from '@core/model/analytic/AnalyticBaseProperties';
import {
  ApplicationInsights,
  ICustomProperties,
  IEventTelemetry,
  ITelemetryItem,
} from '@microsoft/applicationinsights-web';

@Injectable({
  providedIn: 'root',
})
export class AnalyticService {
  properties: AnalyticBaseProperties;
  private applicationInsights: ApplicationInsights = null;

  constructor(
    private applicationStateService: ApplicationStateService,
    public visitStateService: VisitStateService,
    private deviceDetectorService: DeviceDetectorService,
    private heapService: HeapService,
  ) {}

  public init() {
    this.initAppInsights();
    this.initProperties();
    this.initHeap();
  }

  public async trackEvent(
    event: AnalyticCheckinEventTypes,
    properties?: ICustomProperties,
  ): Promise<void> {
    if (isHeapActive()) {
      this.heapService.track(event, Object.assign({}, properties, this.properties));
    }
  }

  public async trackPageView(
    _name: string,
    _uri: string,
    _properties?: ICustomProperties,
  ): Promise<void> {
    return;
  }

  public async trackException(error: Error, properties?: ICustomProperties): Promise<void> {
    const mergedProperties = Object.assign({}, properties, this.properties);
    console.error(error.message, mergedProperties);
  }

  public async startTrackEvent(_name: string): Promise<void> {
    return;
  }

  public async stopTrackEvent(_name: string, _properties?: ICustomProperties): Promise<void> {
    return;
  }

  public async trackAliveEvent(properties?: ICustomProperties): Promise<void> {
    if (this.applicationInsights) {
      const eventBody: IEventTelemetry = {
        name: AnalyticCheckinEventTypes.CheckinDeviceAlive,
      };
      this.applicationInsights?.trackEvent(
        eventBody,
        Object.assign({}, properties, {
          platform: this.properties.Platform,
          appVersion: this.properties.AppVersion,
          trainingCenterId: properties?.trainingCenterId || this.properties.TrainingCenterId,
        }),
      );
    }
  }

  public getPlatformType(): string {
    if (isHostedInWin()) {
      return Platforms.Windows;
    }

    return Platforms.Web;
  }

  private initAppInsights(): void {
    if (environment.appInsights?.instrumentationKey) {
      this.applicationInsights = new ApplicationInsights({
        config: {
          instrumentationKey: environment.appInsights.instrumentationKey,
          disableAjaxTracking: true,
          disableFetchTracking: true,
          disableExceptionTracking: true,
          autoTrackPageVisitTime: false,
        },
      });

      this.applicationInsights?.loadAppInsights();
      this.applicationInsights?.addTelemetryInitializer((envelope: ITelemetryItem) => {
        (envelope.tags as Record<string, string>)['ai.cloud.role'] = AnalyticAppName;
      });
    }
  }
  private initHeap(): void {
    if (environment.heap?.apiKey) {
      this.heapService.initializeHeap(environment.heap.apiKey);
    }
  }

  private initProperties() {
    this.properties = {} as AnalyticBaseProperties;
    this.properties.AppName = AnalyticAppName;
    this.properties.Platform = this.getPlatformType();
    this.initDeviceInformations();
    this.initSessionId();
    this.initStateInfos();
  }

  private initSessionId() {
    this.applicationStateService.sessionId$.subscribe((guid: Guid) => {
      if (guid) {
        this.properties.SessionId = guid.toString();
        this.applicationInsights?.setAuthenticatedUserContext(guid.toString());
      }
    });
  }

  private initStateInfos() {
    combineLatest([
      this.visitStateService.visitorType$,
      this.visitStateService.traineeType$,
      this.applicationStateService.trainingCenter$,
      this.visitStateService.visit$,
      this.visitStateService.training$,
      this.applicationStateService.checkinFlow$,
      this.applicationStateService.hostInfo$,
    ]).subscribe(
      ([visitorType, traineeType, trainingCenter, visit, training, checkinflow, hostInfo]) => {
        this.properties.VisitorType = visitorType?.toString();
        this.properties.TraineeType = traineeType?.toString();
        this.properties.TrainingCenterId = trainingCenter?.geminiSiteId;
        this.properties.VisitId = visit?.id;
        this.properties.TrainingId = training?.id;
        this.properties.flowType = checkinflow?.toString();
        this.properties.kioskSerialNumber = hostInfo?.DeviceSerialNumber;
        this.properties.scannerSerialNumber = hostInfo?.ScannerSerialNumber;
        this.properties.deviceName = hostInfo?.DeviceName;
        this.properties.hostAppVersion = hostInfo?.AppVersion;
        this.properties.AppVersion = environment.buildNumber;
        if (isHeapActive()) {
          this.heapService.addEventProperties(this.properties);
        }
      },
    );
  }

  private initDeviceInformations(): void {
    this.properties.DeviceType = this.deviceDetectorService.device();
    this.properties.ClientOS = this.deviceDetectorService.os();
  }
}
